import { HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { type Extension } from "@codemirror/state";
import { EditorView } from "@codemirror/view";
import { tags as t } from "@lezer/highlight";

/** CodeMirror theme matching the studio's obsidian palette. Colours reference
 *  the same shadcn HSL channel variables (`--foreground`, `--primary`,
 *  `--muted-foreground`) the rest of the app and the read-only Markdown preview
 *  use, so the inline editor reads identically to the preview and follows any
 *  brand-theme swap. Fonts mirror tailwind.config.ts: mono for raw tokens. */
const theme = EditorView.theme(
  {
    "&": {
      color: "hsl(var(--foreground) / 0.9)",
      backgroundColor: "transparent",
      height: "100%",
      // Comfortable default reading size for the writing surface (13px → 16px
      // → 17.5px today).
      // Paired with `.cm-scroller`'s line-height below: styles.css derives
      // `--gm-blank` (the height of one blank source line) from these two
      // numbers to keep the read-only Preview on the same vertical rhythm as
      // Live, so change them together with `.md-column .prose-gm` there.
      fontSize: "17.5px",
      // No focus outline on the editor WRAPPER either — CodeMirror's base theme
      // draws a `.cm-editor.cm-focused` outline, and the app-wide focus ring can
      // otherwise land on the wrapper. The caret is the only focus affordance.
      outline: "none",
    },
    ".cm-scroller": {
      // The writing surface is PROSE, not code — Inter (the site --font-sans),
      // not monospace. Fenced code (`.cm-codeblock`) and inline code (`.cm-code`)
      // re-assert `font-mono` in styles.css, so only actual code stays mono.
      fontFamily: "var(--font-sans)",
      // Leading tuned for the larger 17.5px base — enough air to read, not
      // loose. Unitless so every decorated line (headings at 1.7em, the 0.8em
      // code band) derives its own leading from its own size, and so the
      // Preview's `.prose-gm` — which inherits the same 1.65 — matches it.
      lineHeight: "1.65",
      padding: "16px 0",
      overflow: "auto",
    },
    // Center the editable text in the same readable column as the note's
    // title/cover/preview (.md-column in styles.css): a 54.4rem measure with a
    // 1.5rem inner gutter, centered by `margin: 0 auto`. box-sizing:border-box
    // folds the padding into the 54.4rem so the text's left edge lines up with
    // the title above it, and a narrow/mobile panel degrades to `100% - 3rem`
    // (i.e. min(54.4rem, 100% - 3rem)). The caret, selection, and active-line
    // highlight are painted per `.cm-line` INSIDE this box, so they track it.
    ".cm-content": {
      // Prose font on the writing surface itself (belt-and-suspenders with the
      // scroller above); code decorations override back to mono in styles.css.
      fontFamily: "var(--font-sans)",
      padding: "0 1.5rem",
      // 54.4rem = 64rem × 0.85 — kept in sync with `.md-column` in styles.css.
      maxWidth: "54.4rem",
      margin: "0 auto",
      boxSizing: "border-box",
      caretColor: "hsl(var(--primary))",
    },
    // No boxed edge anywhere on the writing surface: the editable content is a
    // focusable element, so the app-wide `*:focus-visible` ring (styles.css)
    // would draw a 2px box around the whole column when the editor is focused,
    // and CodeMirror draws its own wrapper outline. Suppress every source (the
    // caret is the focus affordance) so the writing area blends into the panel
    // with no highlighted/bordered edge, on both mouse click and keyboard focus.
    ".cm-content:focus, .cm-content:focus-visible": { outline: "none", boxShadow: "none" },
    ".cm-scroller:focus, .cm-scroller:focus-visible": { outline: "none", boxShadow: "none" },
    "&:focus, &:focus-visible": { outline: "none", boxShadow: "none" },
    "&.cm-focused": { outline: "none", boxShadow: "none" },
    // CodeMirror's base theme gives every `.cm-line` a 6px left pad (caret
    // gutter). That pushes the writing text 6px right of the note TITLE and the
    // read-only Preview (both plain `.md-column` children), so Live↔Preview and
    // title↔body don't line up. `.cm-content` already provides the 1.5rem
    // gutter, so zero the line's own left pad for pixel-exact column alignment.
    ".cm-line": { paddingLeft: "0", paddingRight: "2px" },
    ".cm-cursor, .cm-dropCursor": { borderLeftColor: "hsl(var(--primary))" },
    "&.cm-focused .cm-selectionBackground, .cm-selectionBackground, ::selection": {
      backgroundColor: "hsl(var(--primary) / 0.22)",
    },
    ".cm-placeholder": { color: "hsl(var(--muted-foreground) / 0.6)" },
    ".cm-gutters": { display: "none" },
  },
  { dark: true },
);

/** Token colours for source mode and for any markup still visible in live mode
 *  (e.g. the markers revealed under the cursor). The inline rendered styles
 *  (bold/italic/highlight bodies) come from the live-preview decoration classes
 *  in styles.css; this only colours the raw Lezer tokens. */
const highlight = HighlightStyle.define([
  // Headings read white — large/bold enough to anchor the note without colour.
  { tag: t.heading, color: "hsl(var(--foreground))", fontWeight: "600" },
  // Inline **bold** is the ONLY theme-coloured text in the editor.
  { tag: t.strong, fontWeight: "700", color: "hsl(var(--primary))" },
  { tag: t.emphasis, fontStyle: "italic" },
  { tag: t.strikethrough, textDecoration: "line-through" },
  { tag: t.link, color: "hsl(var(--primary))" },
  { tag: t.url, color: "hsl(var(--primary) / 0.7)" },
  { tag: t.monospace, color: "hsl(var(--primary) / 0.9)" },
  { tag: t.quote, color: "hsl(var(--muted-foreground))", fontStyle: "italic" },
  // List markers stay white (not themed) — only bold carries the accent.
  { tag: t.list, color: "hsl(var(--foreground))" },
  // Markdown structural marks (**, ##, >, ==, [[ ]], $) — dim so the prose reads.
  { tag: t.processingInstruction, color: "hsl(var(--muted-foreground))" },
  { tag: t.meta, color: "hsl(var(--muted-foreground))" },
]);

/** The editor's visual theme: palette + token highlighting. */
export const graphmindEditorTheme: Extension = [theme, syntaxHighlighting(highlight)];
